import { useMemo } from 'react';
import {
  getEventModuleLabel,
  isEventModuleEnabled,
  resolveEventModules,
  type EventModuleKey,
} from '@conference/shared';
import { useApp } from '../context/AppContext';

const MODULE_ROUTES: Record<EventModuleKey, string> = {
  agenda: '/agenda',
  papers: '/explore/papers',
  speakers: '/explore/speakers',
  exhibitors: '/explore/exhibitors',
  sponsors: '/explore/sponsors',
  industry_partners: '/explore/industry-partners',
  committee: '/explore/committee',
  attendees: '/explore/attendees',
  venue_map: '/map',
  messaging: '/messages',
  ai_copilot: '/home',
  notes: '/notes',
  recap: '/recap',
};

export function useEventModules() {
  const {
    currentEventId,
    selectedEvent,
    isSelectedEventLoading,
  } = useApp();
  const overrides = selectedEvent?.id === currentEventId
    ? selectedEvent.app_modules
    : undefined;
  const hasConfiguration = !currentEventId || selectedEvent?.id === currentEventId;
  const modules = useMemo(() => resolveEventModules(overrides), [overrides]);
  const enabledModules = useMemo(
    () => hasConfiguration ? modules.filter((module) => module.enabled) : [],
    [hasConfiguration, modules],
  );
  const fallbackModule = enabledModules.find((module) => (
    module.key !== 'recap' || modules.some(({ key, enabled }) => (
      key === 'ai_copilot' && enabled
    ))
  ));

  return {
    modules: hasConfiguration ? modules : [],
    enabledModules,
    isEnabled: (key: EventModuleKey) => (
      hasConfiguration && isEventModuleEnabled(overrides, key)
    ),
    labelFor: (key: EventModuleKey) => getEventModuleLabel(overrides, key),
    fallbackRoute: fallbackModule
      ? MODULE_ROUTES[fallbackModule.key]
      : '/home',
    isReady: Boolean(selectedEvent?.id === currentEventId) || !isSelectedEventLoading,
    hasError: Boolean(currentEventId && !isSelectedEventLoading && !hasConfiguration),
  };
}
